import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import {
  getUserOrganizations,
  grantUserOrgAccess,
  revokeUserOrgAccess,
  ensureUserFederatedId,
} from "./utils/userManagementApi";
import { fetchOrganizationsWithInstallations } from "./utils/graphqlClient";
import logger from "./utils/logger";
import SelfRevokeConfirmModal from "./SelfRevokeConfirmModal";
import "./UserOrgAccessModal.css";
import "./AddUserModal.css"; // Reuse button styles

const UserOrgAccessModal = ({ user, onClose, onAccessUpdated }) => {
  const { getAccessToken, user: currentUser } = useAuth();

  // Debug logging
  logger.log("[UserOrgAccessModal] MODAL OPENED");
  logger.log("[UserOrgAccessModal] Target user:", {
    userId: user?.userId,
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    role: user?.role,
  });
  logger.log("[UserOrgAccessModal] Logged-in admin:", {
    userId: currentUser?.userId,
    email: currentUser?.email,
    roles: currentUser?.roles,
  });

  const [userOrgs, setUserOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // For adding new org
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [allOrgs, setAllOrgs] = useState([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [addingOrg, setAddingOrg] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // For current user's organizations (for permission checks)
  const [currentUserOrgs, setCurrentUserOrgs] = useState([]);
  const [loadingCurrentUserOrgs, setLoadingCurrentUserOrgs] = useState(false);

  // For confirmation modals
  const [showGrantConfirm, setShowGrantConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [grantError, setGrantError] = useState(null);
  const [typedEmail, setTypedEmail] = useState("");

  // For self-revocation
  const [showSelfRevokeModal, setShowSelfRevokeModal] = useState(false);
  const [orgToRevoke, setOrgToRevoke] = useState(null);
  const selectedOrgId = localStorage.getItem("selectedOrgId");

  // Role checks
  const isFullAdmin = currentUser?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === "Full Admin"
  );
  const isOrgAdmin = currentUser?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === "Organization Admin"
  );
  const canManageAccess = isFullAdmin || isOrgAdmin;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Clear any stale localStorage cache when modal opens to ensure fresh data
    logger.log(
      "[UserOrgAccessModal] Clearing localStorage cache on modal open"
    );
    localStorage.removeItem("orgs_cache");
    localStorage.removeItem("orgs_cache_timestamp");

    loadUserOrganizations();
    // If current user is an Org Admin, load their organizations for permission checks
    if (isOrgAdmin && currentUser?.userId) {
      loadCurrentUserOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading && !showOrgSelector) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, loading, showOrgSelector]);

  const loadUserOrganizations = async () => {
    try {
      setLoading(true);
      logger.log("[loadUserOrganizations] ===== LOADING USER ORGS =====");
      logger.log(
        "[loadUserOrganizations] For user:",
        user?.userId,
        user?.email
      );
      const startTime = performance.now();

      // Try optimized approach: Get user's authorized org IDs from cache + DynamoDB
      const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
      const cachedOrgsData = localStorage.getItem("orgs_cache");
      const cacheTimestamp = localStorage.getItem("orgs_cache_timestamp");
      const now = Date.now();

      let allOrgs = null;
      let useOptimizedPath = false;

      // Check if we have valid cached organizations
      if (cachedOrgsData && cacheTimestamp) {
        const age = now - parseInt(cacheTimestamp, 10);
        if (age < CACHE_TTL_MS) {
          try {
            allOrgs = JSON.parse(cachedOrgsData);
            useOptimizedPath = true;
            logger.log(
              `[loadUserOrganizations] Using cached orgs for optimization (age: ${Math.round(
                age / 1000
              )}s)`
            );
          } catch (parseError) {
            logger.warn(
              "[loadUserOrganizations] Failed to parse cached orgs:",
              parseError
            );
          }
        }
      }

      if (useOptimizedPath && allOrgs) {
        // Optimized path: Get user's org IDs from backend (just the IDs, from DynamoDB)
        // Then enrich with cached org details
        logger.log(
          `[loadUserOrganizations] 🚀 OPTIMIZED PATH: Using DynamoDB authorizedOrganizations`
        );
        const result = await getUserOrganizations(user.userId, getAccessToken);
        const elapsed = performance.now() - startTime;
        logger.log("[loadUserOrganizations] API Result:", result);
        logger.log(
          "[loadUserOrganizations] Backend returned",
          result.organizations?.length,
          "orgs for target user"
        );
        logger.log(
          "[loadUserOrganizations] Org IDs from backend:",
          result.organizations?.map((o) => o.orgId)
        );

        // Enrich backend org data with cached org details
        logger.log(
          "[loadUserOrganizations] Enriching with allOrgs cache, cache size:",
          allOrgs?.length
        );
        const enrichedOrgs = result.organizations.map((org) => {
          const cachedOrg = allOrgs.find((o) => o.orgId === org.orgId);
          const enrichedOrg = {
            orgId: org.orgId,
            name: cachedOrg?.name || org.name || null,
            subscriptionStatus:
              cachedOrg?.subscriptionStatus || org.subscriptionStatus,
            authorized: cachedOrg?.authorized ?? org.authorized ?? false,
          };

          if (!enrichedOrg.name) {
            logger.warn("[loadUserOrganizations] ⚠️ ORG WITH NO NAME:", {
              orgId: org.orgId,
              orgFromBackend: org,
              cachedOrgFound: !!cachedOrg,
              cachedOrg: cachedOrg,
            });
          }

          return enrichedOrg;
        });

        logger.log(
          `[loadUserOrganizations] ⚡ OPTIMIZED PATH completed in ${elapsed.toFixed(
            0
          )}ms`
        );
        logger.log(
          `[loadUserOrganizations] Enriched ${enrichedOrgs.length} orgs from cache:`,
          enrichedOrgs.slice(0, 3).map((o) => ({
            orgId: o.orgId,
            name: o.name,
            fromCache: !!allOrgs.find((c) => c.orgId === o.orgId),
          }))
        );

        setUserOrgs(enrichedOrgs);
        logger.log(
          `[loadUserOrganizations] Loaded ${enrichedOrgs.length} user orgs (optimized + enriched)`
        );
      } else {
        // Fallback path: Use existing API (slower but works without cache)
        logger.log(
          "[loadUserOrganizations] ⚠️ LEGACY PATH: No valid cache, using standard API path (may query Yonomi Platform)"
        );
        logger.log(
          "[loadUserOrganizations] Calling getUserOrganizations API for user:",
          user.userId
        );
        const result = await getUserOrganizations(user.userId, getAccessToken);
        logger.log("[loadUserOrganizations] LEGACY PATH result:", result);
        const elapsed = performance.now() - startTime;
        logger.log(
          `[loadUserOrganizations] 🐌 LEGACY PATH completed in ${elapsed.toFixed(
            0
          )}ms`
        );
        logger.log("[loadUserOrganizations] Result:", result);
        logger.log(
          "[loadUserOrganizations] Organizations:",
          result.organizations
        );
        logger.log(
          "[loadUserOrganizations] LEGACY PATH: Setting userOrgs with",
          result.organizations?.length,
          "orgs"
        );
        result.organizations?.forEach((org, idx) => {
          if (!org.name || org.name === null) {
            logger.warn(
              `[loadUserOrganizations] LEGACY PATH: Org at index ${idx} has no name:`,
              org
            );
          }
        });
        setUserOrgs(result.organizations || []);
      }

      logger.log(
        "[loadUserOrganizations] ===== FINISHED LOADING USER ORGS ====="
      );
    } catch (error) {
      logger.error("Failed to load user organizations", error);
      showMessage(`Error loading organizations: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUserOrganizations = async () => {
    try {
      setLoadingCurrentUserOrgs(true);
      logger.log(
        "[loadCurrentUserOrganizations] Loading orgs for current user:",
        currentUser.userId
      );
      const result = await getUserOrganizations(
        currentUser.userId,
        getAccessToken
      );
      logger.log(
        "[loadCurrentUserOrganizations] Current user orgs:",
        result.organizations
      );
      setCurrentUserOrgs(result.organizations || []);
    } catch (error) {
      logger.error("Failed to load current user organizations", error);
      // Don't show error message to user, just log it
    } finally {
      setLoadingCurrentUserOrgs(false);
    }
  };

  const loadAllOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      logger.log(
        "[loadAllOrganizations] ===== LOADING ALL ORGS FOR SELECTOR ====="
      );
      logger.log("[loadAllOrganizations] Logged-in admin:", currentUser?.email);
      logger.log("[loadAllOrganizations] Target user:", user?.email);

      // Try to use cached organizations first (5 minute TTL)
      const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
      const cachedData = localStorage.getItem("orgs_cache");
      const cacheTimestamp = localStorage.getItem("orgs_cache_timestamp");
      const now = Date.now();

      let organizations = null;

      // Use cache if valid and not expired
      if (cachedData && cacheTimestamp) {
        const age = now - parseInt(cacheTimestamp, 10);
        if (age < CACHE_TTL_MS) {
          try {
            organizations = JSON.parse(cachedData);
            logger.log(
              `[loadAllOrganizations] Using cached organizations (age: ${Math.round(
                age / 1000
              )}s)`
            );
          } catch (parseError) {
            logger.warn(
              "[loadAllOrganizations] Failed to parse cached orgs:",
              parseError
            );
          }
        } else {
          logger.log(
            `[loadAllOrganizations] Cache expired (age: ${Math.round(
              age / 1000
            )}s), fetching fresh data`
          );
        }
      }

      // Fetch fresh data if no valid cache
      if (!organizations) {
        logger.log(
          "[loadAllOrganizations] No valid cache, fetching from backend..."
        );
        organizations = await fetchOrganizationsWithInstallations(
          getAccessToken,
          null
          // Don't pass userId here - we're fetching ALL orgs for the org selector,
          // not validating cache for a specific user. The logged-in admin's userId
          // has no relation to the target user whose org access is being managed.
        );
      }

      logger.log("[loadAllOrganizations] Loaded orgs:", organizations);
      logger.log(
        "[loadAllOrganizations] Total orgs for selector:",
        organizations?.length
      );
      logger.log(
        "[loadAllOrganizations] Sample org names:",
        organizations
          ?.slice(0, 5)
          .map((o) => ({ orgId: o.orgId, name: o.name }))
      );
      logger.log(
        "[loadAllOrganizations] Setting allOrgs state with",
        organizations?.length,
        "orgs"
      );
      setAllOrgs(organizations || []);
    } catch (error) {
      logger.error("Failed to load all organizations", error);
      showMessage(`Error loading organizations: ${error.message}`, "error");
    } finally {
      setLoadingOrgs(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleAddOrgClick = async () => {
    setShowOrgSelector(true);
    await loadAllOrganizations();
  };

  const handleGrantAccess = async (orgId, orgName) => {
    logger.log("[handleGrantAccess] Called with:", {
      orgId,
      orgName,
      userId: user.userId,
    });

    // Check if user already has access to this organization
    if (userOrgIds.has(orgId)) {
      logger.log(
        "[handleGrantAccess] User already has access (detected in UI state):",
        { userId: user.userId, orgId, orgName }
      );
      showMessage(
        `${user.firstName} ${user.lastName} already has access to ${orgName}`,
        "error"
      );
      setShowGrantConfirm(false);
      setSelectedOrg(null);
      return;
    }

    try {
      setAddingOrg(true);
      setGrantError(null); // Clear any previous errors

      // First, ensure user has federated ID set
      showMessage(`Checking user setup...`, "info");

      const federatedIdResult = await ensureUserFederatedId(
        user.userId,
        getAccessToken
      );
      logger.log(
        "[handleGrantAccess] Federated ID check result:",
        federatedIdResult
      );

      if (federatedIdResult.wasUpdated) {
        logger.log("[handleGrantAccess] Federated ID was set for user");
      }

      // Now grant org access
      showMessage(
        `Granting organization authorization to ${orgName}...`,
        "info"
      );

      await grantUserOrgAccess(user.userId, orgId, getAccessToken);

      // Invalidate localStorage cache so all components see fresh data
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");
      logger.log("[CACHE_INVALIDATION] Cleared localStorage cache after grant");

      logger.info("Organization access granted", {
        userId: user.userId,
        orgId,
      });
      showMessage(`Access granted to ${orgName}`, "success");
      setShowOrgSelector(false);
      setShowGrantConfirm(false);
      setSelectedOrg(null);
      setGrantError(null);
      loadUserOrganizations();
      // Don't call onAccessUpdated() - keep modal open with refreshed list
    } catch (error) {
      logger.error("Failed to grant organization access", error);

      // Check if error indicates user already has access (stale UI state)
      if (
        error.message?.includes("already assigned") ||
        error.message?.includes("already has access")
      ) {
        setGrantError(
          `${user.firstName} ${user.lastName} already has access to ${orgName}. Refreshing...`
        );
        // Refresh the user's org list to sync with backend
        setTimeout(() => {
          setGrantError(null);
          setShowGrantConfirm(false);
          setShowOrgSelector(false);
          setSelectedOrg(null);
          loadUserOrganizations();
        }, 2000);
      }
      // Check if error message indicates user needs to log in first
      else if (
        error.message?.includes("log in at least once") ||
        error.message?.includes("federated user ID") ||
        error.message?.includes("User does not exist")
      ) {
        setGrantError(
          `${user.firstName} ${user.lastName} must log in at least once before organizations can be assigned.`
        );
      } else {
        setGrantError(`Error granting access: ${error.message}`);
      }
    } finally {
      setAddingOrg(false);
    }
  };

  const handleGrantClick = (orgId, orgName) => {
    setSelectedOrg({ orgId, orgName: orgName || orgId }); // Fallback to orgId if name missing
    setGrantError(null); // Clear any previous errors
    setShowGrantConfirm(true);
  };

  const confirmGrantAccess = () => {
    if (selectedOrg) {
      handleGrantAccess(selectedOrg.orgId, selectedOrg.orgName);
    }
  };

  const handleRevokeAccess = async (orgId, orgName) => {
    try {
      setAddingOrg(true);
      showMessage(
        `Revoking organization authorization from ${orgName}...`,
        "info"
      );

      await revokeUserOrgAccess(user.userId, orgId, getAccessToken);

      // Invalidate localStorage cache so all components see fresh data
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");
      logger.log(
        "[CACHE_INVALIDATION] Cleared localStorage cache after revoke"
      );

      logger.info("Organization access revoked", {
        userId: user.userId,
        orgId,
      });
      showMessage(`Access revoked from ${orgName}`, "success");
      setShowRevokeConfirm(false);
      setSelectedOrg(null);
      loadUserOrganizations();
      // Don't call onAccessUpdated() - keep modal open with refreshed list
    } catch (error) {
      logger.error("Failed to revoke organization access", error);
      showMessage(`Error revoking access: ${error.message}`, "error");
    } finally {
      setAddingOrg(false);
    }
  };

  const confirmRevokeAccess = () => {
    // Validate typed email matches user's email
    if (typedEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
      showMessage(
        "Email does not match. Please type the user's email exactly.",
        "error"
      );
      return;
    }

    if (selectedOrg) {
      handleRevokeAccess(selectedOrg.orgId, selectedOrg.orgName);
    }
  };

  const handleConfirmSelfRevoke = async () => {
    if (!orgToRevoke) return;

    try {
      setAddingOrg(true);
      const { orgId, orgName, isCurrentOrg } = orgToRevoke;

      showMessage(`Revoking your access from ${orgName}...`, "info");

      await revokeUserOrgAccess(user.userId, orgId, getAccessToken);

      // Invalidate localStorage cache
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");
      logger.log(
        "[SELF_REVOCATION] Cleared localStorage cache after self-revoke"
      );

      // Show success message
      showMessage(
        `You've been removed from organization ${orgName}. ${
          isCurrentOrg ? "Please select a different organization." : ""
        }`,
        "success"
      );

      // Close modals
      setShowSelfRevokeModal(false);
      setOrgToRevoke(null);

      // If revoking current org, clear selection and close this modal
      // The OrgInstallWrapper will detect the change and redirect to org selector
      if (isCurrentOrg) {
        localStorage.removeItem("selectedOrgId");
        localStorage.removeItem("selectedInstallationId");
        logger.log(
          "[SELF_REVOCATION] Cleared selectedOrgId and selectedInstallationId"
        );

        // Dispatch custom event to notify OrgInstallWrapper (storage event doesn't fire in same tab)
        window.dispatchEvent(new CustomEvent("selfRevokedCurrentOrg"));

        // Close the modal - DO NOT call onAccessUpdated() as it triggers refresh that interferes with redirect
        onClose();
      } else {
        // Just refresh the list
        loadUserOrganizations();
      }
    } catch (error) {
      logger.error("Failed to revoke self organization access", error);
      showMessage(`Error revoking access: ${error.message}`, "error");
    } finally {
      setAddingOrg(false);
    }
  };

  const handleRevokeClick = (orgId, orgName) => {
    // Check if this is self-revocation (comparing current logged-in user with target user)
    const isSelfRevocation = user.userId === currentUser?.userId;

    // Check if this is the user's last org (prevent last admin scenario)
    if (isSelfRevocation && userOrgs.length === 1) {
      showMessage(
        "Cannot revoke your only organization access. You must belong to at least one organization.",
        "error"
      );
      return;
    }

    // If self-revocation, show special confirmation modal
    if (isSelfRevocation) {
      const isCurrentOrg = orgId === selectedOrgId;
      setOrgToRevoke({ orgId, orgName: orgName || orgId, isCurrentOrg });
      setShowSelfRevokeModal(true);
      return;
    }

    // Standard revocation flow for admins revoking other users
    setSelectedOrg({ orgId, orgName: orgName || orgId });
    setTypedEmail("");
    setShowRevokeConfirm(true);
  };

  const filteredUserOrgs = userOrgs
    .filter((org) => {
      const orgName = org.name || org.orgName || org.orgId || "";
      return orgName.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const nameA = a.name || a.orgName || a.orgId || "";
      const nameB = b.name || b.orgName || b.orgId || "";
      return nameA.localeCompare(nameB);
    });

  const userOrgIds = new Set(userOrgs.map((org) => org.orgId));
  const availableOrgs = allOrgs
    .filter((org) => {
      const orgName = org.name || org.orgName || org.orgId || "";
      return orgName.toLowerCase().includes(orgSearchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const nameA = a.name || a.orgName || a.orgId || "";
      const nameB = b.name || b.orgName || b.orgId || "";
      return nameA.localeCompare(nameB);
    });

  logger.log("[UserOrgAccessModal] Filter debug:", {
    userOrgsCount: userOrgs.length,
    userOrgIds: Array.from(userOrgIds),
    allOrgsCount: allOrgs.length,
    allOrgIds: allOrgs.map((o) => o.orgId),
    allOrgsSample: allOrgs[0],
    availableOrgsCount: availableOrgs.length,
    filteringOut: allOrgs
      .filter((org) => userOrgIds.has(org.orgId))
      .map((o) => ({ id: o.orgId, name: o.name })),
  });

  // Org Admins can only grant/revoke access to orgs they manage
  const currentUserOrgIds = isOrgAdmin
    ? new Set(currentUserOrgs.map((org) => org.orgId))
    : null;

  // Check if target user is Site Admin, Full Admin, or current user (for revoke restrictions)
  const isTargetUserSiteAdmin = user?.role === "Site Admin";
  const isTargetUserFullAdmin = user?.role === "Full Admin";
  const isTargetCurrentUser = user?.userId === currentUser?.userId;

  const canRevokeFromOrg = (orgId) => {
    // Full Admins can revoke their own access, but not other Full Admins
    if (isTargetUserFullAdmin && !isTargetCurrentUser) return false;

    if (isFullAdmin) return true;
    // Org Admins can revoke from orgs they manage, but only for Site Admins or themselves
    if (
      isOrgAdmin &&
      currentUserOrgIds?.has(orgId) &&
      (isTargetUserSiteAdmin || isTargetCurrentUser)
    )
      return true;
    return false;
  };

  const canGrantToOrg = (orgId) => {
    if (isFullAdmin) return true;
    // Org Admins can grant to orgs they manage for any user
    if (isOrgAdmin && currentUserOrgIds?.has(orgId)) return true;
    return false;
  };

  const userHasAccess = (orgId) => {
    return userOrgIds.has(orgId);
  };

  return (
    <div className="user-org-access-modal-overlay">
      <div className="user-org-access-modal">
        <h3>Manage Organization Access</h3>

        <div className="user-org-header">
          <p>
            <strong>User:</strong> {user.firstName} {user.lastName}
          </p>
          <p>
            <strong>Email:</strong> {user.email}
          </p>
          <p>
            <strong>Role:</strong> {user.role}
          </p>
        </div>

        {message && (
          <div className={`org-access-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="org-access-controls">
          <input
            type="text"
            className="org-search-input"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="add-org-btn"
            onClick={handleAddOrgClick}
            disabled={!canManageAccess}
            title={
              !canManageAccess
                ? "You don't have permission to manage access"
                : "Add organization access"
            }
          >
            + Authorize Organization
          </button>
        </div>

        {loading ? (
          <div className="org-access-loading">Loading organizations...</div>
        ) : filteredUserOrgs.length === 0 ? (
          <div className="org-access-empty">
            {searchTerm
              ? "No organizations match your search."
              : "This user has no organization assignments."}
          </div>
        ) : (
          <div className="org-access-list">
            {filteredUserOrgs.map((org) => (
              <div key={org.orgId || org.id} className="org-access-item">
                <div className="org-access-info">
                  <div className="org-access-name">
                    {org.name || org.orgName || "Unknown Organization"}
                  </div>
                  <div className="org-access-id">{org.orgId || org.id}</div>
                </div>
                <button
                  className="revoke-org-btn"
                  onClick={() =>
                    handleRevokeClick(
                      org.orgId || org.id,
                      org.name || org.orgName
                    )
                  }
                  disabled={
                    loadingCurrentUserOrgs ||
                    !canRevokeFromOrg(org.orgId || org.id)
                  }
                  title={
                    loadingCurrentUserOrgs
                      ? "Loading permissions..."
                      : !canRevokeFromOrg(org.orgId)
                      ? isTargetUserFullAdmin && !isTargetCurrentUser
                        ? "Cannot revoke access from other Full Admin users"
                        : isFullAdmin
                        ? "Cannot revoke access"
                        : "Requires Full Admin role to perform this action."
                      : "Revoke access"
                  }
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="org-access-actions">
          <button
            className="close-btn"
            onClick={() => {
              onAccessUpdated();
              onClose();
            }}
          >
            Close
          </button>
        </div>

        {/* Organization Selector Modal */}
        {showOrgSelector && (
          <div className="org-selector-modal-overlay">
            <div className="org-selector-modal">
              <h4>Select Organization</h4>

              <input
                type="text"
                className="org-selector-search"
                placeholder="Search organizations..."
                value={orgSearchTerm}
                onChange={(e) => setOrgSearchTerm(e.target.value)}
              />

              {loadingOrgs ? (
                <div className="org-access-loading">
                  Loading organizations...
                </div>
              ) : availableOrgs.length === 0 ? (
                <div className="org-access-empty">
                  {orgSearchTerm
                    ? "No organizations match your search."
                    : "No available organizations to add."}
                </div>
              ) : (
                <div className="org-selector-list">
                  {availableOrgs.map((org) => {
                    const canAdd = canGrantToOrg(org.orgId);
                    const hasAccess = userHasAccess(org.orgId);
                    return (
                      <div
                        key={org.orgId || org.id}
                        className={`org-selector-item ${
                          !canAdd || addingOrg || hasAccess ? "disabled" : ""
                        }`}
                        onClick={() =>
                          canAdd &&
                          !addingOrg &&
                          !hasAccess &&
                          handleGrantClick(
                            org.orgId || org.id,
                            org.name || org.orgName
                          )
                        }
                        title={
                          hasAccess
                            ? "User already has access to this organization"
                            : addingOrg
                            ? "Processing..."
                            : !canAdd
                            ? "You can only grant access to organizations you manage"
                            : "Click to grant access"
                        }
                      >
                        <div className="org-access-info">
                          <div className="org-access-name">
                            {org.name || "Unnamed"}
                          </div>
                          <div className="org-access-id">{org.orgId}</div>
                        </div>
                        {hasAccess && (
                          <div className="org-authorized-badge">Authorized</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="org-selector-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowOrgSelector(false)}
                  disabled={addingOrg}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grant Access Confirmation Modal */}
      {showGrantConfirm && selectedOrg && (
        <div className="confirm-action-modal-overlay">
          <div className="confirm-action-modal">
            <h3>Confirm Grant Access</h3>
            <p>
              Grant{" "}
              <strong>
                {user.firstName} {user.lastName}
              </strong>{" "}
              access to <strong>{selectedOrg.orgName}</strong>?
            </p>
            <p className="confirm-details">
              This will give them <strong>OWNER_ADMIN</strong> role for this
              organization.
            </p>
            {grantError && (
              <div
                className="org-access-message error"
                style={{ marginTop: "15px" }}
              >
                {grantError}
              </div>
            )}
            {addingOrg && (
              <div className="org-access-loading">Granting access...</div>
            )}
            <div className="confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowGrantConfirm(false);
                  setSelectedOrg(null);
                  setGrantError(null);
                }}
                disabled={addingOrg}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={confirmGrantAccess}
                disabled={addingOrg}
              >
                {addingOrg ? "Granting..." : "Grant Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Access Confirmation Modal */}
      {showRevokeConfirm && selectedOrg && (
        <div className="confirm-action-modal-overlay">
          <div className="confirm-action-modal">
            <h3>Confirm Revoke Access</h3>
            <p>
              Revoke{" "}
              <strong>
                {user.firstName} {user.lastName}'s
              </strong>{" "}
              access to <strong>{selectedOrg.orgName}</strong>?
            </p>
            <p className="warning-text">
              To confirm, please type the user's email address:
            </p>
            <p className="warning-text">{user.email}</p>
            <input
              type="email"
              placeholder={user.email}
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
              className="email-confirm-input"
              autoFocus
            />
            {addingOrg && (
              <div className="org-access-loading">Revoking access...</div>
            )}
            <div className="confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowRevokeConfirm(false);
                  setSelectedOrg(null);
                  setTypedEmail("");
                }}
                disabled={addingOrg}
              >
                Cancel
              </button>
              <button
                className="confirm-btn revoke"
                onClick={confirmRevokeAccess}
                disabled={
                  addingOrg ||
                  typedEmail.trim().toLowerCase() !== user.email.toLowerCase()
                }
              >
                {addingOrg ? "Revoking..." : "Revoke Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-Revocation Confirmation Modal */}
      <SelfRevokeConfirmModal
        isOpen={showSelfRevokeModal}
        onClose={() => {
          setShowSelfRevokeModal(false);
          setOrgToRevoke(null);
        }}
        onConfirm={handleConfirmSelfRevoke}
        orgName={orgToRevoke?.orgName}
        isCurrentOrg={orgToRevoke?.isCurrentOrg}
        loading={addingOrg}
        userEmail={currentUser?.email}
      />
    </div>
  );
};

export default UserOrgAccessModal;
